import { useCallback, useState } from 'react';

import { CompanyEmailUnavailableReason, useCheckCompanyEmailAvailabilityLazyQuery } from '@/common/graphql';

export type EmailCheckStatus =
  | 'idle'
  | 'loading'
  | 'available'
  | 'in_use_med'
  | 'in_use_google_workspace'
  | 'invalid_format'
  | 'api_error';

const NO_IDS: string[] = [];

export const useCheckCompanyEmailAvailability = () => {
  const [status, setStatus] = useState<EmailCheckStatus>('idle');
  const [clearingEmployeeIds, setClearingEmployeeIds] = useState<string[]>(NO_IDS);

  const [executeQuery] = useCheckCompanyEmailAvailabilityLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const check = useCallback(
    async (account: string, domain: string): Promise<void> => {
      setStatus('loading');
      try {
        const { data, error } = await executeQuery({ variables: { account, domain } });
        if (error || !data) {
          setStatus('api_error');
          return;
        }
        const result = data.checkCompanyEmailAvailability;
        if (result.available) {
          setStatus('available');
          setClearingEmployeeIds(result.clearingEmployeeIds ?? NO_IDS);
        } else if (result.unavailableReason === CompanyEmailUnavailableReason.InUseMed) {
          setStatus('in_use_med');
          setClearingEmployeeIds(NO_IDS);
        } else if (result.unavailableReason === CompanyEmailUnavailableReason.InUseGoogleWorkspace) {
          setStatus('in_use_google_workspace');
          setClearingEmployeeIds(result.clearingEmployeeIds ?? NO_IDS);
        } else if (result.unavailableReason === CompanyEmailUnavailableReason.InvalidFormat) {
          setStatus('invalid_format');
          setClearingEmployeeIds(NO_IDS);
        } else {
          setStatus('api_error');
          setClearingEmployeeIds(NO_IDS);
        }
      } catch {
        setStatus('api_error');
        setClearingEmployeeIds(NO_IDS);
      }
    },
    [executeQuery],
  );

  const reset = useCallback(() => {
    setStatus('idle');
    setClearingEmployeeIds(NO_IDS);
  }, []);

  return { status, check, reset, clearingEmployeeIds };
};
